import moment from 'moment'

import {
  APP_TYPE,
  CALENDAR_VIEW_MODE,
  WEBVIEW_TYPES,
} from '@/lib/consts.js'
import { saveLocale } from '@/lib/locale.js'

const CALENDAR_VIEW_MODE_KEY = 'calendar-view-mode'
const APP_COUNTRY_KEY = 'app.country'

export default {
  namespaced: true,

  /*!
   *
   * state
   *
   */
  state: {
    // all supported countries
    supportedCountries: [],

    // current country - from URL
    country: '',

    // current language - from URL
    language: '',

    // current locale - used in API headers and for loading translation files
    // this differs from language - may contain additional dialect code, i.e. "es-es"
    // gets cached as user language selection (per app)
    locale: '',

    // current base URL - including language-country pair
    baseUrl: '',

    // current base path - '/pro' or '' (in development)
    basePath: '',

    // local time of business location
    currentTime: null,

    // application mode: "production" or "staging"
    mode: 'production',

    // "ios", "android" or false
    webView: false,

    appType: APP_TYPE.PWA,

    // calendar view mode
    calendarViewMode: loadCalendarViewMode(),

    previousRoute: null,
    currentRoute: null,

    // x-fingerprint - sent to API on every request
    // used for chaining same device requests across booksy apps
    fingerprint: null,

    // fingerprint passed from the ios app; used in analytics
    ios_fingerprint: null,

    // visibility of application
    isVisible: true,

    // stats & reports url
    statsAndReportsUrl: null,

    // ecommerce url
    ecommerceUrl: null,

    // inventory url
    inventoryUrl: null,

    // financial center url
    booksyWalletUrl: null,

    // booksy wrapped url
    booksyWrappedUrl: null,

    // values moved from `webViewInterface`
    // initial `null` is like `false` (falsy value)) but additionally informs
    // that we didn't request for this value yet
    mobileAppInstalled: null,
  },

  /*!
   *
   * actions
   *
   */
  actions: {
    setup: ({ commit }, data) => {
      commit('setup', data)
    },
  },

  /*!
   *
   * mutations
   *
   */
  mutations: {

    setup (state, {
      supportedCountries,
      country,
      language,
      locale,
      mode,
      webView,
      appType,
      baseUrl,
      basePath,
      fingerprint,
      ios_fingerprint,
      statsAndReportsUrl,
      ecommerceUrl,
      inventoryUrl,
      billingServiceUrl,
      booksyWalletUrl,
      booksyWrappedUrl,
    }) {
      state.supportedCountries = supportedCountries
      state.country = country
      state.language = language
      state.locale = locale
      state.mode = mode
      state.webView = webView
      state.appType = appType
      state.baseUrl = baseUrl
      state.basePath = basePath
      state.fingerprint = fingerprint
      state.ios_fingerprint = ios_fingerprint
      state.statsAndReportsUrl = statsAndReportsUrl
      state.ecommerceUrl = ecommerceUrl
      state.inventoryUrl = inventoryUrl
      state.billingServiceUrl = billingServiceUrl
      state.booksyWalletUrl = booksyWalletUrl
      state.booksyWrappedUrl = booksyWrappedUrl
      saveLocale(state.locale)
      saveCountry(state.country)
    },

    setCurrentTime (state, time) {
      state.currentTime = moment(time)
    },

    setCalendarViewMode (state, mode) {
      if (isValidCalendarViewMode(mode)) {
        state.calendarViewMode = mode
        localStorage.setItem(CALENDAR_VIEW_MODE_KEY, mode)
        return true
      }
      return false
    },

    setRoutes (state, { previousRoute, currentRoute }) {
      state.previousRoute = previousRoute
      state.currentRoute = currentRoute
    },

    // added for a debug purposes
    setPreviousRoute (state, { previousRoute }) {
      state.previousRoute = previousRoute
    },

    setAppVisibilityValue (state, value) {
      state.isVisible = value
    },

    setMobileAppInstalled (state, value) {
      state.mobileAppInstalled = value
    },

    setFingerprint (state, value) {
      state.fingerprint = value
    },
  },

  /*!
   *
   * getters
   *
   */
  getters: {
    isIOS: state => state.webView === WEBVIEW_TYPES.IOS,
    isAndroid: state => state.webView === WEBVIEW_TYPES.ANDROID,
    isTablet: state => state.webView && state.appType === APP_TYPE.TABLET,
    isMobile: state => state.webView && state.appType === APP_TYPE.MOBILE,
    isAndroidTablet: (_, getters) => getters.isAndroid && getters.isTablet,
    appUrl: state => {
      return `${window.location.origin}${state.baseUrl}`
    },

    // for Accept-Language header in API calls
    acceptLanguage: state => {
      if (state.locale) {
        const [language, country] = state.locale.split('-')
        return `${language}${country ? `_${country.toUpperCase()}` : ''}`
      }
      return state.language || 'en'
    },
    country: state => state.country,

    currentRoute: state => state.currentRoute,
  },
}

function isValidCalendarViewMode (mode) {
  return Object.values(CALENDAR_VIEW_MODE).includes(mode)
}

function loadCalendarViewMode () {
  const mode = localStorage.getItem(CALENDAR_VIEW_MODE_KEY)
  return isValidCalendarViewMode(mode) ? mode : CALENDAR_VIEW_MODE.OPTIMIZED
}

function saveCountry (country) {
  localStorage.setItem(APP_COUNTRY_KEY, country)
}
